import { useEffect, useRef, useState } from "react";
import { useOrderDraft } from "../../context/OrderDraftContext";
import { useApi } from "../../hooks/useApi";
import { chatService } from "../../services/chat";
import { saveService } from "../../services/save";
import { showError } from "../../lib/feedback";
import { formatProcedureLines, parseSurgeryRequest } from "../../lib/formatters";

export type OrderStatus = "streaming" | "preparing" | "ready" | "failed";

// O backend fecha o stream com isto quando a IA falha no meio (routers/chat.py → _safe_stream).
export const STREAM_ERROR_MARKER = "[[LETSDOC_STREAM_ERROR]]";

/**
 * Gera o pedido pela IA (streaming) uma vez por conjunto de entradas. Voltar de
 * Dados do paciente ou dar F5 reaproveita o pedido e as edições do médico.
 */
export const useStreamingRequest = () => {
  const context = useOrderDraft();
  const { apiFetch, apiFetchRaw } = useApi();
  const [status, setStatus] = useState<OrderStatus>("streaming");
  const started = useRef(false);

  const items = context.selectedButtonCheckKits === "escolha-livre" ? context.checkedItems : context.checkedKit;
  // Mudou paciente, caso, laudos, cirurgia ou procedimentos: o pedido gerado não vale mais.
  const inputsKey = JSON.stringify([context.patientName, context.newInputRequest, context.laudoText, context.surgery, items]);

  const extract = (text: string) =>
    parseSurgeryRequest(text, {
      desiredDate: context.desiredDate,
      opmeSummary: context.resumoOPME,
      procedures: formatProcedureLines(items),
      hospital: context.hospital,
    });

  const generate = async () => {
    setStatus("streaming");
    context.setEditableResponse("");
    try {
      const response = await chatService.streamOutputChecklist(apiFetchRaw, {
        procedures: items,
        patient_name: context.patientName,
        laudo: `O paciente necessita de uma cirurgia classificada como ${context.surgery}, devido aos seguintes motivos: ${context.laudoText}`,
        historico: context.newInputRequest,
      });

      let text = "";
      let cut = false;
      if (response?.body) {
        const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          text += value;
          if (text.includes(STREAM_ERROR_MARKER)) {
            cut = true;
            text = text.slice(0, text.indexOf(STREAM_ERROR_MARKER)).trimEnd();
          }
          context.setEditableResponse(text);
          context.setSurgeryRequestData(extract(text));
          if (cut) break;
        }
      }

      if (cut || !text.trim()) {
        setStatus("failed");
        showError("stream-interrupted", { retry: generate });
        return;
      }

      context.setOrderInputsKey(inputsKey);
      setStatus("preparing");
      setTimeout(() => setStatus("ready"), 2000);
      saveService.post(apiFetch, { timestamp: context.timestamp, pedido_cirurgia: text }).catch(showError);
    } catch (err) {
      setStatus("failed");
      showError(err, { retry: generate });
    }
  };

  useEffect(() => {
    // StrictMode monta duas vezes: sem isto a IA seria chamada duas vezes.
    if (started.current) return;
    started.current = true;

    // Pedido já gerado com estas entradas (F5, voltou de Dados do paciente): mantém as edições.
    if (context.EditableResponse && context.orderInputsKey === inputsKey) {
      setStatus("ready");
      return;
    }
    // Pedido salvo aberto sem mudar histórico, laudo nem procedimentos: usa o texto salvo.
    const { surgerie } = context;
    if (surgerie && context.EditableResponse && !context.modificadoHistorico && !context.modificadoLaudo && !context.modificadoProcedimentos) {
      context.setSurgeryRequestData(extract(surgerie.pedido_cirurgia));
      context.setOrderInputsKey(inputsKey);
      setStatus("ready");
      return;
    }
    if (context.patientName && context.laudoText && context.newInputRequest) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, retry: generate };
};
